import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { TextTooltip } from "@transfr-inc/dashboard-components";
import {
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";
import {
  DataTableColumnDefinition,
  getUserDetailPathConfig,
} from "@views/users/list/data-table-column-definition";

import "./classroom.prog.leaderboard.table.scss";

const ProgressBar = ({ completedNumerator, exemptNumerator, denominator }) => {
  const progressPercentage =
    denominator > 0 ? (completedNumerator / denominator) * 100 : 0;
  const exemptProgressPercentage =
    denominator > 0 ? (exemptNumerator / denominator) * 100 : 0;

  return (
    <div className="progress-bar-track">
      <div
        className="fill-wrapper"
        style={{ width: `${Math.min(progressPercentage, 100)}%` }}
      >
        <TextTooltip text={`${completedNumerator} Completed Activities`}>
          <div
            className={clsx(
              "progress-bar-fill",
              exemptProgressPercentage > 0 && "flatten-right"
            )}
            aria-label={`${completedNumerator} completed activities out of ${denominator} total activities`}
          />
        </TextTooltip>
      </div>

      <div
        className="fill-wrapper"
        style={{
          width: `${Math.min(exemptProgressPercentage, 100)}%`,
          left: `${Math.min(progressPercentage, 100)}%`,
        }}
      >
        <TextTooltip text={`${exemptNumerator} Exempt Activities`}>
          <div
            className={clsx(
              "progress-bar-fill-exempt",
              progressPercentage > 0 && "flatten-left"
            )}
            aria-label={`${exemptNumerator} exempt activities out of ${denominator} total activities`}
          />
        </TextTooltip>
      </div>
    </div>
  );
};

const ProgressText = ({ numerator, denominator }) => {
  const isComplete = numerator === denominator;

  return isComplete ? (
    <div className="progress-is-complete">
      <FontAwesomeIcon icon="fa-solid fa-circle-check" />
    </div>
  ) : (
    <div className="progress-text">
      {denominator > 0
        ? `${Math.round((numerator / denominator) * 100)}%`
        : "0%"}
    </div>
  );
};

export function ClassroomProgressLeaderboardTable({
  classroom,
  data,
  users,
  responsiveStyles,
}) {
  const { completed_stats, exempt_stats, total_activities } = data;
  const breadcrumbLabel = "Classroom";
  const { classroomId } = useParams();

  const { leaderboardData, isAllProgressZero } = useMemo(() => {
    if (!users || !completed_stats)
      return { leaderboardData: [], isAllProgressZero: true };

    return {
      leaderboardData: users.map((user) => ({
        ...user,
        totalCompletedActivities: completed_stats[user.userId] || 0,
        totalExemptActivities: exempt_stats[user.userId] || 0,
      })),
      isAllProgressZero:
        Object.keys(completed_stats).length === 0 &&
        Object.keys(exempt_stats).length === 0,
    };
  }, [users, completed_stats, exempt_stats]);

  // Key is required to force a re-render of the table when the data changes
  const dataTableKey = useMemo(() => {
    const completedSum = Object.values(completed_stats || {}).reduce(
      (sum, val) => sum + val,
      0
    );
    return `${completedSum}-${total_activities}`;
  }, [completed_stats, total_activities]);

  const columns = useMemo(
    () => [
      DataTableColumnDefinition.AVATAR,
      DataTableColumnDefinition.LAST_NAME(
        classroom?.isProfessionalLearning
          ? undefined
          : getUserDetailPathConfig({
              breadcrumbLabel,
              classroomId,
            })
      ),
      DataTableColumnDefinition.FIRST_NAME(
        classroom?.isProfessionalLearning
          ? undefined
          : getUserDetailPathConfig({
              breadcrumbLabel,
              classroomId,
            })
      ),
      new Column(
        "totalCompletedActivities",
        "Progress",
        ColumnType.CUSTOM,
        new ColumnOptions({
          sortingEnabled: true,
          customFunction: (_value, row) => (
            <ProgressText
              numerator={
                (completed_stats[row.userId] || 0) +
                (exempt_stats[row.userId] || 0)
              }
              denominator={total_activities}
            />
          ),
        })
      ),
      new Column(
        "progressBar",
        "",
        ColumnType.CUSTOM,
        new ColumnOptions({
          sortingEnabled: false,
          customFunction: (_value, row) => (
            <ProgressBar
              completedNumerator={completed_stats[row.userId] || 0}
              exemptNumerator={exempt_stats[row.userId] || 0}
              denominator={total_activities}
            />
          ),
        })
      ),
    ],
    [
      Object.keys(completed_stats).length,
      Object.keys(exempt_stats).length,
      total_activities,
      dataTableKey,
    ]
  );

  return (
    <DataTable
      key={dataTableKey}
      className={clsx("classroom-leaderboard-table", responsiveStyles)}
      columns={columns}
      data={leaderboardData}
      keyFields={["userId"]}
      keyPrefix="course-progress-leaderboard"
      defaultSortPropName={
        isAllProgressZero ? "lastName" : "totalCompletedActivities"
      }
      defaultSortDirection={
        isAllProgressZero ? SortDirection.ASCENDING : SortDirection.DESCENDING
      }
      responsiveStyles={responsiveStyles}
      rowHeightConfig={{ large: 54, medium: 160, small: 280 }}
    />
  );
}
